import { _js } from '@ifixit/utils/localize';
import LoadingIndicator from 'Shared/loading_indicator';
import * as Sentry from '@sentry/browser';
import { ErrorCodes } from 'Shared/localized_API_error_strings';

/**
 * @param endpoint: This should be look like "instances/123". Does not include "/api/2.0/".
 * Options are:
 * onSuccess: function(response) {} Function to be called on success. Response will be an object.
 * method: One of {get, post, put, patch, delete}
 * timeout: ms until request is cancelled.
 *
 * send with .send(data);
 * data will be converted to json and sent in the request body.
 *
 * Example:
 * new Request.API_2_0('badges', {
 *  method: 'get',
 *  onSuccess: function(response) { }
 * }).send();
 */
export default (Request.API_2_0 = new Class({
   Implements: [Options, Events],

   options: {
      timeout: 10_000,
      method: 'get',
      link: 'chain',
      data: '',
      statusPanelMessage: false,
   },

   initialize: function (endpoint, options) {
      this.setOptions(options);

      const requestMethod = this.options.method.toUpperCase();
      const requestHeaders = {};

      // Add a '/' if the endpoint doesn't already have one.
      if (endpoint && endpoint.charAt(0) !== '/') {
         endpoint = '/' + endpoint;
      }

      const uri = endpoint.startsWith('/api/2.0')
         ? new URI(endpoint)
         : new URI('/api/2.0' + endpoint);
      if (App.apiUrlParams && Object.keys(App.apiUrlParams).length) {
         uri.setData(App.apiUrlParams, /* merge with existing */ true);
      }

      if (this.options.queryParams) {
         uri.setData(this.options.queryParams, /* merge with existing */ true);
      }

      this.request = new Request({
         url: uri,
         method: requestMethod,
         headers: requestHeaders,
         link: this.options.link,
         data: this.options.data,
         emulation: false,
         onSuccess: this.onSuccess.bind(this),
         onFailure: xhr => {
            const message = this.getErrorMessage(xhr);
            this.fireEvent('failure', [xhr, message]);
            this._reject(message);
         },
         onCancel: (...args) => {
            Request.activeRequests.delete(this);
            this.fireEvent('cancel', args);
            this._reject();
         },
         onComplete: (...args) => {
            Request.activeRequests.delete(this);
            Request.completedRequests++;
            this.fireEvent('complete', args);
         },
         withCredentials: true,
      });
   },

   send: function (data) {
      const promise = new Promise((resolve, reject) => {
         this._resolve = resolve;
         this._reject = reject;

         getRequestHeaders().forEach(header => this.request.setHeader(header.key, header.value));

         Request.activeRequests.set(this, {
            url: this.request.options.url,
            method: this.options.method,
            data: data,
         });

         if (this.options.method == 'get') {
            this.request.send(data);
         } else {
            // Send data as a json blob.
            this.request.send(JSON.stringify(data));
         }
      });
      if (this.options.statusPanelMessage) {
         LoadingIndicator.withPromise(promise, {
            pendingMessage: this.options.statusPanelMessage,
            failureMessage: _js('Error'),
            hideSuccessMessage: true,
            useErrorResponseAsFailureMessage: true,
            duration: 4000,
         });
      }
      return promise;
   },

   getErrorMessage: function (xhr) {
      let message = this.getErrorMessageByStatusCode(xhr.status);

      if (!message) {
         let defaultMessage = _js('An unknown error occurred.');
         const response = this.jsonParseSafely(xhr.response);

         if (!response) {
            return _js('A networking error occurred.');
         }

         if (typeof response == 'string') {
            defaultMessage = response;
         } else if (response.message) {
            defaultMessage = response.message;
         }

         // Replace API error with available localized string.
         const localized_UI_message = this.getLocalizedMessage(response);
         if (localized_UI_message) {
            return localized_UI_message;
         }

         // Use the first error message if one exists or a default message.
         message = response.errors ? response.errors[0].message : defaultMessage;
      }

      return message;
   },

   getLocalizedMessage: response => {
      const response_code = response.errors?.[0]?.code;
      const localized_UI_message = ErrorCodes[response_code];
      return localized_UI_message;
   },

   getErrorMessageByStatusCode: statusCode => {
      const messages = {
         409: _js(
            'Your changes could not be saved because the version of the document you are editing is out of date. Reload the page and try again.'
         ),
         429: _js('You have made too many requests recently.'),
      };

      return messages[statusCode];
   },

   onSuccess: function (text) {
      let response;
      try {
         response = text && typeOf(text) == 'string' ? JSON.parse(text) : null;
      } catch (e) {
         // Make sure this try/catch doesn't affect behavior. We still want
         // this to be an unhandled exception (I think). It's certainly easier
         // in development when you get a big red banner.
         this.reportToSentry(e, this.request.xhr);
         this._reject();
         throw e;
      }

      if (response && response.code >= 400) {
         this.fireEvent('onError', [response]);
         this._reject(response);
         if (window.console && console.error) {
            console.error(response.message);
         }
      } else {
         this.fireEvent('onSuccess', [response]);
         this._resolve(response);
      }
   },

   reportToSentry: function (err, xhr) {
      Sentry.captureException(err, {
         contexts: {
            api_response: {
               snippet: xhr.response.slice(0, 200),
               length: xhr.response.length,
            },
            api_request: {
               url: xhr.responseURL,
               status: xhr.status,
               method: this.options.method,
            },
         },
      });
   },

   jsonParseSafely: response => {
      try {
         return JSON.parse(response);
      } catch {
         return null;
      }
   },
}));

function getRequestHeaders() {
   const headers = [
      // This enables the API over HTTP so browsers without CORS can use it.
      { key: 'X-ALLOW-HTTP', value: true },
      // Identifies _this request for API stat purposes.
      { key: 'Api-Client', value: 'iFixit-Web' },
   ];

   return headers;
}
